import React, { useState, useEffect } from 'react';
import { listNotes, createNote, deleteNote } from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';

const OrderNotes = ({ orderId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [orderId]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await listNotes('order', orderId);
      if (response && response.success) {
        setNotes(response.notes || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      showToast({ type: 'error', message: 'Failed to load notes' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        content: newNote.trim()
      };

      const response = await createNote('order', orderId, payload);
      if (response && response.success) {
        setNewNote('');
        showToast({ type: 'success', message: 'Note added successfully' });
        fetchNotes(); // reload
      } else {
        showToast({ type: 'error', message: response?.error || 'Failed to add note' });
      }
    } catch (error) {
      console.error('Error creating note:', error);
      showToast({ type: 'error', message: 'Failed to add note' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const response = await deleteNote(noteId);
      if (response && response.success) {
        setNotes(notes.filter(n => n.id !== noteId));
        showToast({ type: 'success', message: 'Note deleted' });
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast({ type: 'error', message: 'Failed to delete note' });
    }
  };

  return (
    <div className="orderNotesContainer" style={{
      background: '#fff',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginTop: '0'
    }}>
      <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: '#333' }}>Order Notes</h3>

      {/* Note Input */}
      <form onSubmit={handleCreateNote} style={{ marginBottom: '20px' }}>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '10px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            marginBottom: '10px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
          disabled={isSubmitting}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={isSubmitting || !newNote.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: newNote.trim() && !isSubmitting ? '#2563eb' : '#9ca3af',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: newNote.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      </form>

      {/* Notes List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>Loading notes...</div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontStyle: 'italic', background: '#f9fafb', borderRadius: '6px' }}>
          No notes for this order yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {notes.map(note => (
            <div key={note.id} style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>
                    {note.created_by?.name || note.created_by?.email || 'Unknown User'}
                  </span>
                  <span style={{ color: '#6b7280', fontSize: '0.8rem', marginLeft: '10px' }}>
                    {new Date(note.created_at).toLocaleString('en-IN')}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}
                  title="Delete note"
                >
                  Delete
                </button>
              </div>
              <div style={{ color: '#374151', fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {note.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderNotes;
